import { PERMISSIONS } from "@/constants/permissions"
import { SETTINGS_PATHS } from "@/features/settings/paths"
import { useGuardedMutation } from "@/hooks/useGuardedMutation"
import { apiClientV2 } from "@/services/api/apiClient"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

// GET/PUT /api/v2/material-compliance/settings/regulations — org-level
// regulation subscriptions ("Regelwerke" settings section). See
// backend/turnus_ai/api/v2/mc_regulation_settings.py for the contract.
const BASE = "/material-compliance/settings/regulations"

export const REGULATION_SETTINGS_QUERY_KEY = ["mc", "settings", "regulations"]

/** Deep link to the Regelwerke settings page. */
export const ORG_REGULATIONS_SETTINGS_ROUTE = SETTINGS_PATHS.regulations

export type RegulationReason =
  | "customer_requirement"
  | "legal_obligation"
  | "proactive"

export type RegulationSource = "explicit" | "inferred" | null

export type RegulationNotPickableReason =
  | "no_regulation_row"
  | "no_copy"
  | "no_register"
  | "not_supported"
  | null

// ─── Wire shapes (apiClientV2 converts snake_case <-> camelCase) ──────────────

export interface RegulationSettingRow {
  /** null only when no mc_regulations row exists for this name. */
  regulationId: string | null
  /** Canonical UPPER name, e.g. "REACH", "ROHS". */
  name: string
  /** normalize_framework_code(name) — feed to regulationLabel() for display. */
  frameworkCode: string | null
  /** Conformity v2 framework keys this regulation maps to. */
  frameworkKeys: string[]
  /** Regulation NAMES this one depends on (e.g. PFAS -> ["REACH_ANNEX_XVII"]). */
  requires: string[]
  /** mc_regulations.jurisdiction, e.g. ["EU", "EEA"], ["CN"], ["US-CA"]. */
  jurisdiction: string[]
  subscribed: boolean
  source: RegulationSource
  reason: RegulationReason | null
  requiredSince: string | null
  pickable: boolean
  notPickableReason: RegulationNotPickableReason
}

export interface RegulationSettingsOut {
  regulations: RegulationSettingRow[]
}

export interface RegulationSubscriptionIn {
  regulationId: string
  reason: RegulationReason
  /** ISO date string. Omitted, not null, when the row has none — the backend
   *  default is None either way. */
  requiredSince?: string
}

async function fetchRegulationSettings(): Promise<RegulationSettingsOut> {
  const { data } = await apiClientV2.get<RegulationSettingsOut>(BASE)
  return data
}

/** GET — every CMS-supported regulation (plus any org-subscribed one that
 *  fell out of support), with subscription/provenance/availability state. */
export function useRegulationSettings(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: REGULATION_SETTINGS_QUERY_KEY,
    queryFn: fetchRegulationSettings,
    enabled: options?.enabled ?? true,
  })
}

export async function saveRegulationSettingsRequest(
  subscriptions: RegulationSubscriptionIn[],
): Promise<RegulationSettingsOut> {
  const { data } = await apiClientV2.put<RegulationSettingsOut>(BASE, {
    subscriptions,
  })
  return data
}

// Queries whose data follows the org's regulation set (TUR-1734).
const ORG_SET_QUERY_KEYS = [
  ["mc", "regulations", "list"],
  ["mc", "supplier-dashboard"],
  ["mc", "customer-dashboard"],
]

/** PUT — full replace of the org's regulation subscriptions. Promotes any
 *  inferred rows the caller included into explicit ones (see backend PUT
 *  rules). Invalidates the settings query and every conformity v2 preview,
 *  since the pickable/subscribed set changes what those can offer, plus what
 *  follows the org set: the regulation list (master-data filters order and
 *  `inOrgSet`) and both Studio dashboards' regulation rows. */
export function useSaveRegulationSettings() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: saveRegulationSettingsRequest,
    onSuccess: (data) => {
      qc.setQueryData(REGULATION_SETTINGS_QUERY_KEY, data)
      qc.invalidateQueries({
        queryKey: ["mc", "conformity", "v2", "preview"],
      })
      for (const queryKey of ORG_SET_QUERY_KEYS) {
        qc.invalidateQueries({ queryKey })
      }
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_MASTER_DATA_EDIT)
}
